import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ExtensionApiFactory, IExtensionApiStrategy } from '~cdw-ae/common/frontend/browser-extension-api';

import { DeviceResource } from '../check-in/check-in.interfaces';
import { BaseInitialState, StateStatus } from '../extension-store/interfaces/extension-store.interfaces';

export interface DeviceDetails {
  id: string;
  assetId?: string;
  serialNumber: string;
  OSVersion?: string;
  deviceOrgUnitPath?: string;
  latestStableVersion?: string;
  platformVersion?: string;
  chromeOSVersionMax?: number;
}

interface InitialDeviceState extends BaseInitialState {
  details: DeviceDetails;
  deviceResource?: DeviceResource;
  detailStatus: StateStatus;
  resourceStatus: StateStatus;
}

const initialState: InitialDeviceState = {
  error: null,
  status: StateStatus.IDLE,
  detailStatus: StateStatus.IDLE,
  resourceStatus: StateStatus.IDLE,
  details: {
    id: '',
    assetId: '',
    serialNumber: '',
    OSVersion: '',
  },
};

const isStatusComplete = (status: InitialDeviceState): StateStatus =>
  status.detailStatus === StateStatus.SUCCESS && status.resourceStatus === StateStatus.SUCCESS
    ? StateStatus.SUCCESS
    : StateStatus.LOADING;

export const DeviceEffects = {
  fetchDeviceDetails: createAsyncThunk('DeviceDetails/fetchDeviceDetails', async (): Promise<DeviceDetails> => {
    const extensionApi: IExtensionApiStrategy = ExtensionApiFactory();
    return extensionApi.getDevice();
  }),
};

export const DeviceDetailsState = createSlice({
  name: 'DeviceDetails',
  initialState,
  reducers: {
    setId: (mutableState, action: PayloadAction<string>) => {
      mutableState.details.id = action.payload;
    },
    setAssetId: (mutableState, action: PayloadAction<string>) => {
      mutableState.details.assetId = action.payload;
    },
    setSerialNumber: (mutableState, action: PayloadAction<string>) => {
      mutableState.details.serialNumber = action.payload;
    },
    setOSVersion: (mutableState, action: PayloadAction<string>) => {
      mutableState.details.OSVersion = action.payload;
    },
    setDeviceResource: (mutableState, action: PayloadAction<DeviceResource>) => {
      mutableState.deviceResource = action.payload;
      mutableState.resourceStatus = StateStatus.SUCCESS;
      mutableState.status = isStatusComplete(mutableState);
    },
  },
  extraReducers(builder: ActionReducerMapBuilder<InitialDeviceState>) {
    builder.addCase(DeviceEffects.fetchDeviceDetails.pending, (mutableState) => {
      mutableState.status = StateStatus.LOADING;
      mutableState.detailStatus = StateStatus.LOADING;
    });
    builder.addCase(
      DeviceEffects.fetchDeviceDetails.fulfilled,
      (mutableState, action: PayloadAction<DeviceDetails>) => {
        mutableState.details = action.payload;
        mutableState.detailStatus = StateStatus.SUCCESS;
        mutableState.status = isStatusComplete(mutableState);
      }
    );
    builder.addCase(DeviceEffects.fetchDeviceDetails.rejected, (mutableState, action) => {
      mutableState.detailStatus = StateStatus.FAILED;
      mutableState.error = action.error;
    });
  },
});

export const { setId, setAssetId, setOSVersion, setSerialNumber, setDeviceResource } = DeviceDetailsState.actions;
